import React from "react";
import { Button, Tooltip } from "@src/common/components";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/outline";
import {
  useFinalizeOrderPricingMutation,
  UserRoleType,
  useUnfinalizeOrderPricingMutation,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";

interface PricingFinalizationControlsProps {
  isFinalized: boolean;
  orderId: string;
  onFinalizationChange?: () => void;
}

export function PricingFinalizationControls({
  isFinalized,
  orderId,
  onFinalizationChange,
}: PricingFinalizationControlsProps) {
  const { courierId, isRoleType } = useAuth();
  const showError = useErrorToast();

  const [finalizeOrderPricingMutation, { loading: finalizeLoading }] =
    useFinalizeOrderPricingMutation({
      onCompleted: () => {
        onFinalizationChange?.();
      },
      onError: (error) => {
        showError(error);
      },
    });

  const [unfinalizeOrderPricingMutation, { loading: unfinalizeLoading }] =
    useUnfinalizeOrderPricingMutation({
      onCompleted: () => {
        onFinalizationChange?.();
      },
      onError: (error) => {
        showError(error);
      },
    });

  const handleToggleFinalization = () => {
    if (!courierId) {
      console.error("Courier ID is required for finalization");
      return;
    }

    const variables = { courierId, orderId };

    if (isFinalized) {
      unfinalizeOrderPricingMutation({ variables });
    } else {
      finalizeOrderPricingMutation({ variables });
    }
  };

  const isLoading = finalizeLoading || unfinalizeLoading;
  const isDisabled = isLoading || !courierId;
  const tooltipContent = !courierId
    ? "Courier ID required"
    : isFinalized
    ? "Unlock pricing"
    : "Lock pricing";

  if (!isRoleType(UserRoleType.Administrator)) {
    return null;
  }

  return (
    <Tooltip content={tooltipContent}>
      <Button
        color={"gray"}
        size="xs"
        onClick={handleToggleFinalization}
        disabled={isDisabled}
        isProcessing={isLoading}
      >
        {isFinalized ? (
          <LockClosedIcon className="h-4 w-4" />
        ) : (
          <LockOpenIcon className="h-4 w-4" />
        )}
      </Button>
    </Tooltip>
  );
}
